import { useState } from "react";
import parse, { Element, DOMNode, domToReact } from "html-react-parser";
import Dialog from "./v2/dialog";
import { CopyableCode, extractText } from "./v2/copyable-code";
import { renderHeadingWithSectionLink } from "../lib/blog-section-anchors";

function findPre(node: DOMNode | DOMNode[] | undefined): Element | null {
  if (!node) return null;
  if (Array.isArray(node)) {
    for (const child of node) {
      const found = findPre(child);
      if (found) return found;
    }
    return null;
  }
  if ((node as Element).name === "pre") return node as Element;
  if ((node as Element).children) {
    return findPre((node as Element).children as DOMNode[]);
  }
  return null;
}

function hasClass(el: Element | null | undefined, className: string) {
  return (el?.attribs?.class || "").split(/\s+/).includes(className);
}

export default function PostBody({ content }) {
  const [imageOpen, setImageOpen] = useState(false);
  const [image, setImage] = useState<string | null>(null);
  const [imageAlt, setImageAlt] = useState("");

  const handleImageClick = (src: string, alt?: string) => {
    setImage(src);
    setImageAlt(alt || "");
    setImageOpen(true);
  };

  const options = {
    replace: (domNode: Element) => {
      if (domNode.name === "script") {
        return <></>;
      }

      if (domNode.name === "img") {
        const { src, alt, width, height } = domNode.attribs;
        const parsedWidth = width ? parseInt(width, 10) : undefined;
        const parsedHeight = height ? parseInt(height, 10) : undefined;
        const hasDimensions =
          parsedWidth &&
          parsedHeight &&
          !Number.isNaN(parsedWidth) &&
          !Number.isNaN(parsedHeight);

        return (
          <img
            src={src}
            alt={alt || ""}
            loading="lazy"
            decoding="async"
            width={hasDimensions ? parsedWidth : 864}
            height={hasDimensions ? parsedHeight : 486}
            style={{
              cursor: "zoom-in",
              maxWidth: "100%",
              height: "auto",
              aspectRatio: hasDimensions
                ? `${parsedWidth} / ${parsedHeight}`
                : "16 / 9",
            }}
            onClick={() => handleImageClick(src, alt)}
          />
        );
      }

      if (domNode.name === "div" && hasClass(domNode, "content-copy")) {
        const pre = findPre(domNode.children as DOMNode[]);
        const text = extractText((pre?.children as DOMNode[]) || (domNode.children as DOMNode[]));
        return (
          <CopyableCode text={text}>
            {pre ? (
              <pre>{domToReact(pre.children as DOMNode[])}</pre>
            ) : (
              <pre>{extractText(domNode.children as DOMNode[])}</pre>
            )}
          </CopyableCode>
        );
      }

      if (domNode.name === "pre") {
        const parent = domNode.parent as Element | null;
        if (hasClass(parent, "content-copy") || hasClass(parent, "code-block")) {
          return;
        }
        const text = extractText(domNode.children as DOMNode[]);
        return (
          <CopyableCode text={text}>
            <pre>{domToReact(domNode.children as DOMNode[])}</pre>
          </CopyableCode>
        );
      }
    },
  };

  return (
    <>
      <div className="post-body">{parse(content || "", options)}</div>

      <Dialog
        isOpen={imageOpen}
        position="center"
        onClose={() => setImageOpen(false)}
        size="md"
        verticalCenter={true}
      >
        <div style={{ textAlign: "center" }}>
          {image && (
            <img
              src={image}
              alt={imageAlt}
              style={{ maxWidth: "100%", height: "auto" }}
            />
          )}
        </div>
      </Dialog>
    </>
  );
}
